import { TFunction } from 'i18next'

import { ChatState } from '@app/hooks'
import { ConversationItem, MessageItem, Participant } from '@app/models'

import { getBaseInfo } from './getBaseInfo'
import { getIsB2BOrCurrentUserPrivateAdvertiser } from './getIsB2BOrCurrentUserPrivateAdvertiser'

const isAgent = (participant?: Participant | null) =>
  participant?.role === 'Agent' || participant?.role === 'MainAgent'

const resolveParticipantName = (
  participant: Participant | undefined,
  conversation: ConversationItem | undefined,
  state: ChatState
) => {
  if (!participant || !conversation) {
    return undefined
  }

  const { fullName } = getBaseInfo(state, participant, conversation)

  return fullName || participant.fullName
}

export const getSystemMessageTranslation = (
  message: MessageItem,
  conversation: ConversationItem | undefined,
  t: TFunction,
  state: ChatState,
  currentUserId?: number | string,
  isB2B = false,
  regionalLanguage?: string,
  isStaffUser = false
): string | null => {
  const currentUserIdNumber =
    currentUserId !== undefined && currentUserId !== null
      ? Number(currentUserId)
      : undefined

  if (message?.systemNotificationType === 'Call') {
    const participants = conversation?.participants || []

    const callerParticipantId = message.callerParticipantId

    const fromParticipant = participants.find(
      (participant) => participant.id === callerParticipantId
    )

    const toParticipant = participants.find(
      (participant) => participant.id === message.toParticipantId
    )

    const currentUserParticipant = participants.find(
      (participant) =>
        currentUserIdNumber !== undefined &&
        participant.userId === currentUserIdNumber
    )

    const isCaller =
      currentUserParticipant?.id &&
      currentUserParticipant.id === callerParticipantId

    const isCallee =
      currentUserParticipant?.id &&
      currentUserParticipant.id === message.toParticipantId

    const shouldShowCallMessage = isCaller || isCallee || isStaffUser

    if (!shouldShowCallMessage) return null

    const translationKey = isCaller
      ? 'Chat.SystemNotification.Call.BuyerOrRenter'
      : 'Chat.SystemNotification.Call.Agent'

    const targetParticipant = isCaller ? toParticipant : fromParticipant

    if (!targetParticipant) return null

    const name = resolveParticipantName(targetParticipant, conversation, state)

    if (!name) return null

    return t(translationKey, { name })
  }

  if (message?.systemNotificationType === 'UnrepliedNotificationSent') {
    if (conversation?.participants?.length) {
      const participants = conversation.participants
      const isB2BOrCurrentUserPrivateAdvertiser =
        getIsB2BOrCurrentUserPrivateAdvertiser(
          participants,
          currentUserId,
          isB2B
        )

      const agent = participants.find(
        (participant) =>
          participant.id === message.recipientParticipantId ||
          isAgent(participant)
      )

      const buyerOrRenter = participants.find(
        (participant) => participant.id === message.buyerOrRenterParticipantId
      )

      const translationKey = isB2BOrCurrentUserPrivateAdvertiser
        ? 'Chat.UnrepliedConversationsNotification.AgentChatMessage'
        : 'Chat.UnrepliedConversationsNotification.BuyerOrRenterChatMessage'

      const nameParticipant = isB2BOrCurrentUserPrivateAdvertiser
        ? buyerOrRenter
        : agent
      const name = resolveParticipantName(nameParticipant, conversation, state)

      if (name) {
        return t(translationKey, { name })
      }
    }

    return null
  }

  const translations = message?.translations ?? []

  if (!translations.length) {
    return message?.content?.text ?? null
  }

  const translation = translations.find(
    (item) => item.language === regionalLanguage
  )

  if (translation?.text?.trim()) {
    return translation.text
  }

  const fallbackText = message?.content?.text

  return fallbackText?.trim() ? fallbackText : null
}
